import { ApiError } from "../api.js";
import { esc, certLevelOptionsHtml, toast } from "../utils.js";
import { confirmAction } from "../confirmDialog.js";
import { navigate } from "../app.js";
import { IMPERIAL, METRIC, unitsOf } from "../units.js";
import {
  deleteAccount,
  updateDisplayName,
  updateBio,
  updateCertificationLevel,
  updateUnits,
  changePassword,
  getCurrentUser,
  MAX_BIO_LENGTH,
} from "../auth.js";

export function render(container) {
  const me = getCurrentUser();

  container.innerHTML = `
    <h1 class="display-heading h3 mb-4">Edit Profile</h1>

    <h2 class="h5 mb-2">Display Name</h2>
    <form id="name-form" class="mb-4">
      <div class="mb-3">
        <input type="text" class="form-control" id="display-name-input" value="${esc(me?.display_name ?? "")}" required />
      </div>
      <div id="name-error" class="text-danger small mb-2 d-none"></div>
      <div id="name-success" class="alert alert-success py-2 px-3 mb-2 d-none" role="status"></div>
      <button type="submit" class="btn btn-primary" id="name-submit">Save Display Name</button>
    </form>

    <hr class="my-4" />

    <h2 class="h5 mb-2">About You</h2>
    <form id="bio-form" class="mb-4">
      <div class="mb-2">
        <label class="form-label" for="bio-input">
          A short description shown on your profile
        </label>
        <!-- A textarea, not an input: line breaks are kept and the
             profile renders them. -->
        <textarea class="form-control" id="bio-input" rows="5"
                  maxlength="${MAX_BIO_LENGTH}"
                  placeholder="Where you dive, what you are working towards, anything you want other divers to know.">${esc(me?.bio ?? "")}</textarea>
        <div class="form-text"><span id="bio-count">0</span> / ${MAX_BIO_LENGTH}</div>
      </div>
      <div id="bio-error" class="text-danger small mb-2 d-none"></div>
      <div id="bio-success" class="alert alert-success py-2 px-3 mb-2 d-none" role="status"></div>
      <button type="submit" class="btn btn-primary" id="bio-submit">Save Description</button>
    </form>

    <hr class="my-4" />

    <h2 class="h5 mb-2">Certification</h2>

    <form id="cert-form" class="mb-4">
      <div class="mb-2">
        <label class="form-label" for="cert-input">Certification level</label>
        <select class="form-select" id="cert-input" style="max-width:22rem;">
          ${certLevelOptionsHtml(me?.certification_level ?? null)}
        </select>
        <div class="form-text">Shown on your profile. Choose “Not set” to remove it.</div>
      </div>
      <div id="cert-error" class="text-danger small mb-2 d-none"></div>
      <div id="cert-success" class="alert alert-success py-2 px-3 mb-2 d-none" role="status"></div>
      <button type="submit" class="btn btn-primary" id="cert-submit">Save Certification</button>
    </form>

    <hr class="my-4" />

    <h2 class="h5 mb-2">Units</h2>
    <form id="units-form" class="mb-4">
      <fieldset class="mb-2">
        <legend class="form-label fs-6">How depths, temperatures and pressures are shown to you, and how you enter them</legend>
        <div class="form-check">
          <input class="form-check-input" type="radio" name="units" id="units-metric" value="${METRIC}"
                 ${unitsOf(me) === METRIC ? "checked" : ""} />
          <label class="form-check-label" for="units-metric">Metric (m, °C, bar)</label>
        </div>
        <div class="form-check">
          <input class="form-check-input" type="radio" name="units" id="units-imperial" value="${IMPERIAL}"
                 ${unitsOf(me) === IMPERIAL ? "checked" : ""} />
          <label class="form-check-label" for="units-imperial">Imperial (ft, °F, psi)</label>
        </div>
        <div class="form-text">Each dive also has a m / ft switch, to see it the other way.</div>
      </fieldset>
      <div id="units-error" class="text-danger small mb-2 d-none"></div>
      <div id="units-success" class="alert alert-success py-2 px-3 mb-2 d-none" role="status"></div>
      <button type="submit" class="btn btn-primary" id="units-submit">Save Units</button>
    </form>

    <hr class="my-4" />

    <h2 class="h5 mb-2">Change Password</h2>
    <form id="password-form">
      <div class="mb-3">
        <label class="form-label" for="current-password-input">Current password</label>
        <input type="password" class="form-control" id="current-password-input" required />
      </div>
      <div class="mb-3">
        <label class="form-label" for="new-password-input">New password</label>
        <input type="password" class="form-control" id="new-password-input" required minlength="8" />
        <div class="form-text">At least 8 characters.</div>
      </div>
      <div class="mb-3">
        <label class="form-label" for="confirm-password-input">Confirm new password</label>
        <input type="password" class="form-control" id="confirm-password-input" required />
      </div>
      <div id="password-error" class="text-danger small mb-2 d-none"></div>
      <div id="password-success" class="text-success small mb-2 d-none"></div>
      <button type="submit" class="btn btn-primary" id="password-submit">Update Password</button>
    </form>

    <hr class="my-4" />
    <h2 class="h5 mb-2 text-danger" id="delete-account">Delete Account</h2>
    <p class="text-slate small mb-2">Deletes your account at once, with your posts, comments, photos, reactions,
      the dive events you organized, and who you follow. Dive sites you added stay, without your name.
      This can't be undone. <a href="privacy.html#delete-account" target="_blank" rel="noopener">More in the Privacy Policy</a>.</p>
    <form id="delete-form" novalidate>
      <div class="mb-2">
        <label class="form-label" for="delete-password-input">Your password</label>
        <input type="password" class="form-control" id="delete-password-input" autocomplete="current-password"
          style="max-width:22rem;" />
      </div>
      <div id="delete-error" class="text-danger small mb-2 d-none"></div>
      <button type="submit" class="btn btn-outline-danger" id="delete-submit">Delete my account</button>
    </form>`;

  /** Shows a confirmation that cannot be missed.
   *
   * role="status" so a screen reader announces it; the check mark and the
   * alert background carry it visually. */
  function showSaved(el, message) {
    el.innerHTML = `<i class="bi bi-check-circle-fill me-2"></i>${esc(message)}`;
    el.classList.remove("d-none");
  }

  /** A confirmation next to a field the user has since changed is a lie,
   *  so it is cleared on the next keystroke. */
  function clearOnEdit(input, ...messages) {
    input.addEventListener("input", () => {
      messages.forEach((el) => el.classList.add("d-none"));
    });
  }

  const nameForm = container.querySelector("#name-form");
  const nameError = container.querySelector("#name-error");
  const nameSuccess = container.querySelector("#name-success");
  const nameSubmit = container.querySelector("#name-submit");

  clearOnEdit(container.querySelector("#display-name-input"), nameSuccess, nameError);

  nameForm.addEventListener("submit", async (e) => {
    e.preventDefault();
    nameError.classList.add("d-none");
    nameSuccess.classList.add("d-none");
    const value = container.querySelector("#display-name-input").value.trim();
    if (!value) {
      nameError.textContent = "Display name is required.";
      nameError.classList.remove("d-none");
      return;
    }
    nameSubmit.disabled = true;
    try {
      await updateDisplayName(value);
      showSaved(nameSuccess, "Display name saved.");
    } catch (err) {
      nameError.textContent = err instanceof ApiError ? err.message : "Something went wrong.";
      nameError.classList.remove("d-none");
    } finally {
      nameSubmit.disabled = false;
    }
  });

  const bioForm = container.querySelector("#bio-form");
  const bioInput = container.querySelector("#bio-input");
  const bioError = container.querySelector("#bio-error");
  const bioSuccess = container.querySelector("#bio-success");
  const bioSubmit = container.querySelector("#bio-submit");
  const bioCount = container.querySelector("#bio-count");

  function paintCount() {
    bioCount.textContent = String(bioInput.value.length);
  }
  bioInput.addEventListener("input", paintCount);
  paintCount();
  clearOnEdit(bioInput, bioSuccess, bioError);

  bioForm.addEventListener("submit", async (e) => {
    e.preventDefault();
    bioError.classList.add("d-none");
    bioSuccess.classList.add("d-none");
    bioSubmit.disabled = true;
    try {
      await updateBio(bioInput.value);
      // Clearing it is a legitimate edit, so say so either way.
      showSaved(
        bioSuccess,
        bioInput.value.trim() ? "Description saved." : "Description removed.",
      );
    } catch (err) {
      bioError.textContent = err instanceof ApiError ? err.message : "Something went wrong.";
      bioError.classList.remove("d-none");
    } finally {
      bioSubmit.disabled = false;
    }
  });

  const certForm = container.querySelector("#cert-form");
  const certInput = container.querySelector("#cert-input");
  const certError = container.querySelector("#cert-error");
  const certSuccess = container.querySelector("#cert-success");
  const certSubmit = container.querySelector("#cert-submit");

  clearOnEdit(certInput, certSuccess, certError);

  certForm.addEventListener("submit", async (e) => {
    e.preventDefault();
    certError.classList.add("d-none");
    certSuccess.classList.add("d-none");
    certSubmit.disabled = true;
    try {
      await updateCertificationLevel(certInput.value);
      // Clearing it is a legitimate edit, so say so either way — the
      // same reasoning as the description above.
      showSaved(
        certSuccess,
        certInput.value ? "Certification level saved." : "Certification level removed.",
      );
    } catch (err) {
      certError.textContent = err instanceof ApiError ? err.message : "Something went wrong.";
      certError.classList.remove("d-none");
    } finally {
      certSubmit.disabled = false;
    }
  });

  const unitsForm = container.querySelector("#units-form");
  const unitsError = container.querySelector("#units-error");
  const unitsSuccess = container.querySelector("#units-success");
  const unitsSubmit = container.querySelector("#units-submit");

  unitsForm.addEventListener("change", () => {
    unitsSuccess.classList.add("d-none");
    unitsError.classList.add("d-none");
  });

  unitsForm.addEventListener("submit", async (e) => {
    e.preventDefault();
    unitsError.classList.add("d-none");
    unitsSuccess.classList.add("d-none");
    const chosen = unitsForm.querySelector('input[name="units"]:checked')?.value ?? METRIC;
    unitsSubmit.disabled = true;
    try {
      await updateUnits(chosen);
      showSaved(unitsSuccess, chosen === IMPERIAL ? "Units saved: imperial." : "Units saved: metric.");
    } catch (err) {
      unitsError.textContent = err instanceof ApiError ? err.message : "Something went wrong.";
      unitsError.classList.remove("d-none");
    } finally {
      unitsSubmit.disabled = false;
    }
  });

  const passwordForm = container.querySelector("#password-form");
  const passwordError = container.querySelector("#password-error");
  const passwordSuccess = container.querySelector("#password-success");
  const passwordSubmit = container.querySelector("#password-submit");

  passwordForm.addEventListener("submit", async (e) => {
    e.preventDefault();
    passwordError.classList.add("d-none");
    passwordSuccess.classList.add("d-none");

    const current = container.querySelector("#current-password-input").value;
    const next = container.querySelector("#new-password-input").value;
    const confirm = container.querySelector("#confirm-password-input").value;

    if (next.length < 8) {
      passwordError.textContent = "New password must be at least 8 characters.";
      passwordError.classList.remove("d-none");
      return;
    }
    if (next !== confirm) {
      passwordError.textContent = "Passwords do not match.";
      passwordError.classList.remove("d-none");
      return;
    }

    passwordSubmit.disabled = true;
    try {
      await changePassword(current, next);
      passwordForm.reset();
      passwordSuccess.textContent = "Password updated.";
      passwordSuccess.classList.remove("d-none");
    } catch (err) {
      passwordError.textContent = err instanceof ApiError ? err.message : "Something went wrong.";
      passwordError.classList.remove("d-none");
    } finally {
      passwordSubmit.disabled = false;
    }
  });

  const deleteForm = container.querySelector("#delete-form");
  const deleteError = container.querySelector("#delete-error");
  const deleteSubmit = container.querySelector("#delete-submit");

  deleteForm.addEventListener("submit", async (e) => {
    e.preventDefault();
    deleteError.classList.add("d-none");
    const password = container.querySelector("#delete-password-input").value;
    if (!password) {
      deleteError.textContent = "Enter your password to delete your account.";
      deleteError.classList.remove("d-none");
      return;
    }
    const ok = await confirmAction({
      title: "Delete your account?",
      body: "Your account and everything you've posted are deleted now. This can't be undone.",
      confirmLabel: "Delete my account",
    });
    if (!ok) return;

    deleteSubmit.disabled = true;
    try {
      await deleteAccount(password);
      toast("Your account has been deleted.", "success");
      navigate("/login");
    } catch (err) {
      deleteError.textContent = err instanceof ApiError ? err.message : "Something went wrong.";
      deleteError.classList.remove("d-none");
      deleteSubmit.disabled = false;
    }
  });
}
